import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import LinkButton from '../../button/LinkButton';
import { PageRoutes } from '../../../../features/app/pages/domain/constants/PageRoutes';
import { Color } from '../../../../styles/Color';
import {
	ArrowForwardIcon,
	Css,
	ExternalLinkIcon,
	ILinkItemType,
	ITheme,
	Responsive,
} from '../../../../ui';

interface IContentCardProps {
	title: string;
	subtitle?: string;
	body?: ReactNode | string;
	buttonLabel?: string;
	buttonLink?: string;
	linkType?: ILinkItemType;
}

export default function ContentCard({
	title,
	subtitle,
	body,
	buttonLabel,
	buttonLink,
	linkType,
}: IContentCardProps) {
	const opensInNewTab = linkType === 'external-link';

	return (
		<ContentCardStyled aria-labelledby={`card-title-${title}`}>
			{subtitle && (
				<span className="overline" id={`card-subtitle-${title}`}>
					{subtitle}
				</span>
			)}
			<ContentCardTitle id={`card-title-${title}`}>{title}</ContentCardTitle>
			<CardBody aria-describedby={`card-subtitle-${title}`}>{body}</CardBody>
			{buttonLink && (
				<CardButtonWrapper>
					<LinkButton
						href={buttonLink || PageRoutes.Gateway}
						aria-label={`Learn more about ${title} ${
							opensInNewTab ? `(opens in a new tab)` : ''
						}`}
						icon={
							linkType === 'external-link' ? (
								<ExternalLinkIcon aria-hidden />
							) : (
								<ArrowForwardIcon aria-hidden />
							)
						}
						{...(linkType === 'external-link'
							? { target: '_blank', rel: 'noopener noreferrer' }
							: {})}
					>
						{buttonLabel}
					</LinkButton>
				</CardButtonWrapper>
			)}
		</ContentCardStyled>
	);
}

const ContentCardStyled = styled.div<{}, ITheme>`
	padding: ${Css.pxToRem(32)};
	margin: 0;
	background-color: ${Color.Neutral2};
	border-radius: ${(props) => props.theme.card?.borderRadius};
	box-shadow: ${(props) => props.theme.card?.fullyClickable?.boxShadow};
	border: 1px solid ${Color.BorderCard};
	overflow: hidden;

	.overline {
		letter-spacing: ${Css.pxToRem(1)};
	}

	a {
		text-decoration: none;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		padding: ${Css.pxToRem(16)};
	}
`;

const ContentCardTitle = styled.h4`
	margin-bottom: 0;
	line-height: normal;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(32)};
	}
`;

const CardBody = styled.div`
	font-size: ${Css.pxToRem(16)};
	margin-top: ${Css.pxToRem(8)};
	color: ${Color.Neutral6};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		font-size: ${Css.pxToRem(16)};
	}
`;

const CardButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${Css.pxToRem(24)};

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: ${Css.pxToRem(16)};
	}
`;
